import React, { useMemo } from 'react';
import { View, Pressable, Platform, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useRouter } from 'expo-router';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Badge from '@/components/Typography/Badge';
import TaskTemplateFavoriteHeart from '@/components/Tasks/TaskTemplateFavoriteHeart';
import { AGENT_ICON } from '@/constants/config';
import { getAgentImageBaseFilter } from '@/constants/Colors';
import { getPaletteForCategorySlug, heroChromaFromPaletteFg, accentHexFromPaletteFg } from '@/components/Tools/toolDetailHelpers';
import { colorForSlug, heroChromaForSlug, breadcrumbHoverTintForSlug } from '@/utils/agent_catalog';

/** Pick label/icon color on top of a solid hex or rgba (CTA, chips). */
function pickContrastOnCategoryFg(colorStr) {
  const s = String(colorStr || '');
  const rgb = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  let r = 99;
  let g = 102;
  let b = 241;
  if (rgb) {
    r = +rgb[1];
    g = +rgb[2];
    b = +rgb[3];
  } else if (/^#([0-9a-f]{6})$/i.test(s)) {
    const hex = s.slice(1);
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.55 ? '#111827' : '#FFFFFF';
}

const IS_WEB = Platform.OS === 'web';

const STATIC_STYLES = StyleSheet.create({
  imageFill: { width: '100%', height: '100%' },
  webCursorPointer: IS_WEB ? { cursor: 'pointer' } : {},
  badgePillBase: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 999,
    minHeight: 22,
    height: undefined,
    paddingVertical: 3,
    paddingHorizontal: 10,
  },
  plusPill: {
    borderRadius: 999,
    minHeight: 22,
    height: undefined,
    paddingHorizontal: 10,
  },
  badgePrefixCluster: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
});

function CatalogDetailHero({
  backLink = null,
  categorySlug = null,
  /**
   * Secondary slug for `colorForSlug` when `categorySlug` is empty (e.g. tool slug).
   * Accent / CTA / washes use `categorySlug` first when set, else this, else a stable default.
   */
  gradientSlug = null,
  palette: paletteProp = null,
  topline = null,
  eyebrowBadge = null,
  headline,
  headlineTag = 'h1',
  /** Web-only `id` for SEO speakable markup on topic pages. */
  headlineNativeId = null,
  subtitle = null,
  /** Web-only `id` for SEO speakable markup on topic pages. */
  subtitleNativeId = null,
  heroIcon = null,
  avatar = null,
  primaryAction = null,
  favoriteTemplate = null,
  favoriteAccessory = null,
  badgeRowPrefix = null,
  metaItems = null,
  /**
   * Optional category segment after the back control: "… / [icon] Label".
   * Pass `onPress` and/or in-app `href` (must start with `/`) for a tappable crumb; omit both for plain text.
   * When set, the hero topline chip is hidden to avoid duplication.
   * Optional `accessibilityLabel` overrides the default for the link crumb.
   */
  catalogCategory = null,
  /** Max width (px) for the hero content column; omit for full-bleed width. */
  heroMaxWidth = null,
  /**
   * Optional rounded-card framing. When set, the hero becomes a bordered card
   * with this corner radius instead of the default full-bleed band — used to
   * visually match a cover image / form below it (e.g. the prompt detail page).
   */
  borderRadius = null,
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { isXS, isSM } = useResponsiveness();

  const palette = useMemo(
    () => paletteProp || getPaletteForCategorySlug(categorySlug),
    [paletteProp, categorySlug],
  );

  /** Matches list/detail accents: category first, then item slug, then stable fallback. */
  const accentSlug = useMemo(() => {
    const c = categorySlug != null ? String(categorySlug).trim() : '';
    if (c) return c;
    const g = gradientSlug != null ? String(gradientSlug).trim() : '';
    return g;
  }, [categorySlug, gradientSlug]);

  const usePaletteAccent = paletteProp != null;

  const chromaHex = useMemo(() => {
    if (usePaletteAccent) {
      const h = accentHexFromPaletteFg(palette.fg);
      if (h) return h;
    }
    return colorForSlug(accentSlug || '_');
  }, [usePaletteAccent, palette.fg, accentSlug]);

  const primaryCtaBg = usePaletteAccent ? palette.fg : chromaHex;

  const ctaOnFg = useMemo(
    () => pickContrastOnCategoryFg(usePaletteAccent ? palette.fg : chromaHex),
    [usePaletteAccent, palette.fg, chromaHex],
  );

  const heroChroma = useMemo(
    () => (usePaletteAccent ? heroChromaFromPaletteFg(palette.fg) : heroChromaForSlug(accentSlug || '_')),
    [usePaletteAccent, palette.fg, accentSlug],
  );

  const breadcrumbHoverStyle = useMemo(
    () => ({
      backgroundColor: usePaletteAccent ? palette.bg : breadcrumbHoverTintForSlug(accentSlug || '_'),
    }),
    [usePaletteAccent, palette.bg, accentSlug],
  );

  const newBadgeStyle = useMemo(
    () => [STATIC_STYLES.badgePillBase, { borderColor: colors.border_color_secondary }],
    [colors.border_color_secondary],
  );

  const heroInnerMaxStyle = useMemo(() => {
    const raw = heroMaxWidth;
    if (raw === undefined || raw === null || raw === '') {
      return { width: '100%', alignSelf: 'center' };
    }
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/px/gi, '').trim(), 10);
    if (Number.isNaN(n) || n <= 0) return { width: '100%', alignSelf: 'center' };
    return { width: '100%', alignSelf: 'center', maxWidth: n };
  }, [heroMaxWidth]);

  const showBack = backLink && typeof backLink.onPress === 'function' && !!backLink.label;
  const showSubtitle = !!(subtitle && String(subtitle).trim());
  const showAvatar = avatar != null && typeof avatar === 'object';
  const showLeading = showAvatar || !!heroIcon;
  const catalogCategoryLabel =
    catalogCategory && String(catalogCategory.label || '').trim()
      ? String(catalogCategory.label).trim()
      : '';
  const hasCatalogCategory = !!catalogCategoryLabel;
  const categoryCrumbIsLink =
    hasCatalogCategory &&
    (typeof catalogCategory?.onPress === 'function' ||
      (typeof catalogCategory?.href === 'string' &&
        catalogCategory.href.trim().length > 0 &&
        catalogCategory.href.trim().startsWith('/')));
  const showToplineChip =
    !!(topline && String(topline).trim()) && !hasCatalogCategory;

  const invertImage = !!(showAvatar && avatar.invertImage);
  const baseFilter = useMemo(
    () => getAgentImageBaseFilter(theme, invertImage),
    [theme, invertImage],
  );
  const avatarWebFilterStyle = useMemo(() => {
    if (Platform.OS !== 'web' || !invertImage || !baseFilter) return undefined;
    return { filter: baseFilter };
  }, [invertImage, baseFilter]);
  const avatarNativeImageFilterStyle = useMemo(() => {
    if (Platform.OS === 'web' || !invertImage || !baseFilter) return undefined;
    return { filter: baseFilter };
  }, [invertImage, baseFilter]);

  const normalizedMeta = useMemo(() => {
    if (!Array.isArray(metaItems)) return [];
    return metaItems.filter((row) => row && String(row.label || '').trim());
  }, [metaItems]);

  const showFavoriteSlot = !!(favoriteAccessory || favoriteTemplate);
  const showBadgeRow =
    !!badgeRowPrefix ||
    eyebrowBadge === 'new' ||
    eyebrowBadge === 'plus' ||
    showFavoriteSlot ||
    normalizedMeta.length > 0;
  const showPrimaryCta = primaryAction != null && typeof primaryAction.onPress === 'function';
  const primaryDisabled = !!(primaryAction && primaryAction.disabled);

  const sBase = useMemo(
    () =>
      StyleSheet.create({
        breadcrumbRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          marginBottom: isXS ? 14 : 16,
        },
        breadcrumbBtn: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingVertical: 6,
          paddingHorizontal: 8,
          borderRadius: 6,
          marginLeft: -8,
        },
        heroIconBox: {
          width: isXS ? 60 : 72,
          height: isXS ? 60 : 72,
          borderRadius: 18,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: palette.bg,
          borderWidth: 1,
          borderColor: palette.bg,
          overflow: 'hidden',
        },
        breadcrumbSep: {
          marginHorizontal: 2,
          paddingHorizontal: 4,
          paddingVertical: 2,
        },
        categoryCrumbRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingVertical: 4,
          paddingHorizontal: 8,
          borderRadius: 8,
        },
      }),
    [isXS, palette.bg],
  );

  const categoryCrumbOnPress = useMemo(() => {
    if (!categoryCrumbIsLink || !hasCatalogCategory) return undefined;
    if (typeof catalogCategory?.onPress === 'function') return catalogCategory.onPress;
    const href = String(catalogCategory?.href || '').trim();
    if (!href) return undefined;
    return () => {
      if (href.startsWith('/')) router.push(href);
    };
  }, [catalogCategory, categoryCrumbIsLink, hasCatalogCategory, router]);

  const backCrumbStyle = useMemo(
    () => (state) => [
      sBase.breadcrumbBtn,
      STATIC_STYLES.webCursorPointer,
      state.hovered && IS_WEB && breadcrumbHoverStyle,
    ],
    [sBase.breadcrumbBtn, breadcrumbHoverStyle],
  );

  const categoryCrumbStyle = useMemo(
    () => (state) => [
      sBase.categoryCrumbRow,
      STATIC_STYLES.webCursorPointer,
      state.hovered && IS_WEB && breadcrumbHoverStyle,
    ],
    [sBase.categoryCrumbRow, breadcrumbHoverStyle],
  );

  const catalogBreadcrumbRow = useMemo(() => {
    if (!showBack && !hasCatalogCategory) return null;
    return (
      <View style={sBase.breadcrumbRow}>
        {showBack ? (
          <Pressable onPress={backLink.onPress} accessibilityRole="link" style={backCrumbStyle}>
            <Icon name="chevron-left" size={12} color={colors.text_color_secondary} />
            <Text size="s" weight="medium" color="secondary">
              {backLink.label}
            </Text>
          </Pressable>
        ) : null}
        {showBack && hasCatalogCategory ? (
          <Text size="s" color="tertiary" style={sBase.breadcrumbSep}>
            /
          </Text>
        ) : null}
        {hasCatalogCategory ? (
          categoryCrumbIsLink ? (
            <Pressable
              onPress={categoryCrumbOnPress}
              accessibilityRole="link"
              accessibilityLabel={catalogCategory?.accessibilityLabel || catalogCategoryLabel}
              style={categoryCrumbStyle}
            >
              <Icon name={catalogCategory?.icon || 'folder'} size={12} color={colors.text_color_secondary} />
              <Text size="s" weight="semibold" numberOfLines={1} color="primary">
                {catalogCategoryLabel}
              </Text>
            </Pressable>
          ) : (
            <View style={sBase.categoryCrumbRow}>
              <Icon name={catalogCategory?.icon || 'folder'} size={12} color={colors.text_color_secondary} />
              <Text size="s" weight="medium" color="secondary" numberOfLines={1}>
                {catalogCategoryLabel}
              </Text>
            </View>
          )
        ) : null}
      </View>
    );
  }, [
    showBack,
    hasCatalogCategory,
    sBase,
    backLink,
    backCrumbStyle,
    colors.text_color_secondary,
    categoryCrumbIsLink,
    categoryCrumbOnPress,
    categoryCrumbStyle,
    catalogCategory,
    catalogCategoryLabel,
  ]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        heroWrap: {
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: colors.border_color_secondary,
          backgroundColor: colors.solid_color_primary,
        },
        heroGradient: { ...StyleSheet.absoluteFillObject },
        heroGradientWash: { ...StyleSheet.absoluteFillObject },
        heroBlob: {
          position: 'absolute',
          width: isXS ? 280 : 360,
          height: isXS ? 280 : 360,
          borderRadius: 400,
          top: isXS ? -100 : -118,
          right: isXS ? -72 : -88,
          backgroundColor: heroChroma.blobA,
          opacity: Platform.OS === 'web' ? 0.52 : 0.5,
          ...(Platform.OS === 'web' ? { filter: 'blur(44px)' } : null),
        },
        heroBlobSecondary: {
          position: 'absolute',
          width: isXS ? 200 : 260,
          height: isXS ? 200 : 260,
          borderRadius: 300,
          bottom: isXS ? -110 : -130,
          left: isXS ? -70 : -90,
          backgroundColor: heroChroma.blobB,
          opacity: Platform.OS === 'web' ? 0.38 : 0.36,
          ...(Platform.OS === 'web' ? { filter: 'blur(36px)' } : null),
        },
        heroInner: {
          padding: isXS ? 16 : 24,
          paddingTop: isXS ? 16 : 22,
          paddingBottom: isXS ? 24 : 32,
          alignSelf: 'center',
          width: '100%',
        },
        heroMain: {
          flexDirection: isXS ? 'column' : 'row',
          alignItems: 'flex-start',
          gap: isXS ? 10 : 15,
        },
        heroKickerRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 2,
        },
        categoryChip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingVertical: 5,
          paddingHorizontal: 11,
          borderRadius: 999,
          backgroundColor: palette.bg,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border_color_secondary,
        },
        leadFrame: {
          borderRadius: 20,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border_color_secondary,
          backgroundColor: colors.solid_color_primary,
          ...(Platform.OS === 'web'
            ? {
                boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
              }
            : {
                elevation: 3,
              }),
        },
        heroTitle: {
          fontSize: isXS ? 26 : isSM ? 30 : 35,
          lineHeight: isXS ? 31 : isSM ? 35 : 40,
          letterSpacing: isXS ? -0.35 : -0.45,
          marginTop: isXS ? 10 : 0,
          flexShrink: 1,
        },
        heroBadgeRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        },
        metaChip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 5,
          paddingVertical: 5,
          paddingHorizontal: 11,
          borderRadius: 999,
          backgroundColor: colors.solid_color_primary,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border_color_secondary,
        },
        primaryCta: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          minHeight: 40,
          paddingVertical: 10,
          paddingHorizontal: 18,
          borderRadius: 999,
          backgroundColor: primaryCtaBg,
          ...(Platform.OS === 'web'
            ? {
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.2s ease, opacity 0.2s ease, transform 0.15s ease',
              }
            : null),
        },
        primaryCtaText: {
          fontSize: 14,
          fontWeight: '700',
          letterSpacing: 0.15,
        },
        heroHeadings: {
          flex: 1,
          minWidth: 0,
          gap: isXS ? 15 : 10,
        },
        heroSubtitle: {
          lineHeight: isXS ? 22 : 24,
          maxWidth: 720,
          marginTop: 2,
        },
        actionsRow: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginTop: 5,
        },
      }),
    [colors, isXS, isSM, palette, heroChroma, primaryCtaBg],
  );

  const primaryCtaStyle = useMemo(
    () => ({ hovered, pressed }) => [
      styles.primaryCta,
      primaryDisabled && { opacity: 0.65 },
      hovered && IS_WEB && !primaryDisabled && {
        opacity: 1,
        boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
      },
      pressed && !primaryDisabled && IS_WEB && { transform: [{ scale: 0.98 }] },
      pressed && !primaryDisabled && !IS_WEB && { opacity: 0.88 },
    ],
    [styles.primaryCta, primaryDisabled],
  );

  const primaryCtaTextStyle = useMemo(
    () => [styles.primaryCtaText, { color: ctaOnFg }],
    [styles.primaryCtaText, ctaOnFg],
  );

  const primaryCtaEl = useMemo(() => {
    if (!showPrimaryCta) return null;
    return (
      <Pressable
        onPress={primaryAction.onPress}
        disabled={primaryDisabled}
        style={primaryCtaStyle}
        accessibilityRole="button"
        accessibilityLabel={primaryAction.accessibilityLabel || primaryAction.label}
        accessibilityState={{ disabled: primaryDisabled }}
      >
        <Icon name={primaryAction.icon} size={17} color={ctaOnFg} />
        <Text style={primaryCtaTextStyle}>{primaryAction.label}</Text>
      </Pressable>
    );
  }, [showPrimaryCta, primaryAction, primaryDisabled, primaryCtaStyle, primaryCtaTextStyle, ctaOnFg]);

  const leadingFrameStyle = useMemo(
    () => [sBase.heroIconBox, styles.leadFrame],
    [sBase.heroIconBox, styles.leadFrame],
  );

  const avatarWrapStyle = useMemo(
    () => [STATIC_STYLES.imageFill, avatarWebFilterStyle],
    [avatarWebFilterStyle],
  );

  const avatarImageStyle = useMemo(
    () => [STATIC_STYLES.imageFill, avatarNativeImageFilterStyle],
    [avatarNativeImageFilterStyle],
  );

  const leadingVisual = useMemo(() => {
    if (!showLeading) return null;
    return (
      <View style={leadingFrameStyle}>
        {showAvatar ? (
          <View style={avatarWrapStyle}>
            {avatar.uri ? (
              <Image
                source={{ uri: avatar.uri, width: isXS ? 60 : 72, height: isXS ? 60 : 72 }}
                style={avatarImageStyle}
                contentFit="cover"
                priority="high"
                transition={IS_WEB ? 0 : undefined}
              />
            ) : (
              <Icon name={AGENT_ICON} size={isXS ? 28 : 34} color={palette.fg} />
            )}
          </View>
        ) : (
          <Icon name={heroIcon} size={isXS ? 28 : 34} color={palette.fg} solid />
        )}
      </View>
    );
  }, [
    showLeading,
    showAvatar,
    avatar,
    avatarWrapStyle,
    avatarImageStyle,
    heroIcon,
    isXS,
    leadingFrameStyle,
    palette.fg,
  ]);

  const badgeRow = useMemo(
    () => (
      <>
        {badgeRowPrefix ? <View style={STATIC_STYLES.badgePrefixCluster}>{badgeRowPrefix}</View> : null}
        {eyebrowBadge === 'new' ? (
          <Badge
            text={t('pages.tools.badgeNew') || 'New'}
            isSmall
            textColor="secondary"
            backgroundColor={colors.solid_color_tertiary}
            style={newBadgeStyle}
          />
        ) : null}
        {eyebrowBadge === 'plus' ? (
          <Badge text="PLUS" isPlus isSmall style={STATIC_STYLES.plusPill} />
        ) : null}
        {favoriteAccessory
          ? favoriteAccessory
          : favoriteTemplate
            ? <TaskTemplateFavoriteHeart template={favoriteTemplate} />
            : null}
        {normalizedMeta.map((row, idx) => (
          <View key={`${row.icon}-${row.label}-${idx}`} style={styles.metaChip}>
            <Icon name={row.icon || 'circle'} size={11} color={colors.text_color_secondary} />
            <Text size="xxs" weight="semibold" color="secondary" numberOfLines={1}>
              {row.label}
            </Text>
          </View>
        ))}
      </>
    ),
    [
      badgeRowPrefix,
      eyebrowBadge,
      t,
      colors.solid_color_tertiary,
      colors.text_color_secondary,
      newBadgeStyle,
      favoriteAccessory,
      favoriteTemplate,
      normalizedMeta,
      styles.metaChip,
    ],
  );

  return (
    <View
      style={[
        styles.heroWrap,
        borderRadius != null ? {
          borderRadius,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border_color_secondary,
        } : null,
      ]}
    >
      <LinearGradient
        colors={[colors.solid_color_tertiary, 'transparent']}
        locations={[0, 1]}
        start={{ x: 0.5, y: 1 }}
        end={{ x: 0.5, y: 0.2 }}
        style={styles.heroGradientWash}
        pointerEvents="none"
      />
      <LinearGradient
        colors={[heroChroma.gradSoft, heroChroma.gradMid, 'transparent']}
        locations={[0, 0.2, 1]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.heroGradient}
        pointerEvents="none"
      />
      <View style={styles.heroBlob} pointerEvents="none" />
      <View style={styles.heroBlobSecondary} pointerEvents="none" />
      <View style={[styles.heroInner, heroInnerMaxStyle]}>
        {catalogBreadcrumbRow}
        <View style={styles.heroMain}>
          {leadingVisual}
          <View style={styles.heroHeadings}>
            {showToplineChip ? (
              <View style={styles.heroKickerRow}>
                <View style={styles.categoryChip}>
                  <Icon name="tag" size={11} color={palette.fg} />
                  <Text size="xs" weight="bold" style={{ color: palette.fg }} numberOfLines={2}>
                    {topline}
                  </Text>
                </View>
              </View>
            ) : null}
            <Text
              tag={headlineTag}
              weight="extraBold"
              color="primary"
              style={styles.heroTitle}
              {...(headlineNativeId && IS_WEB ? { id: headlineNativeId } : {})}
            >
              {headline}
            </Text>
            {showBadgeRow ? <View style={styles.heroBadgeRow}>{badgeRow}</View> : null}
            {showSubtitle ? (
              <Text
                size={isXS ? 'n' : 'm'}
                color="secondary"
                style={styles.heroSubtitle}
                {...(subtitleNativeId && IS_WEB ? { id: subtitleNativeId } : {})}
              >
                {subtitle}
              </Text>
            ) : null}
            {showPrimaryCta ? <View style={styles.actionsRow}>{primaryCtaEl}</View> : null}
          </View>
        </View>
      </View>
    </View>
  );
}

export default React.memo(CatalogDetailHero);
